import {
    Text,
    VStack,
    HStack,
    Switch,
    IconButton,
    Icon,
    Select,
    Box,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ModalCloseButton,
    AlertDialog,
    AlertDialogBody,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogCloseButton,
    AlertDialogContent,
    AlertDialogOverlay,
    Button,
    Input,
    InputGroup,
    InputLeftElement,
    Checkbox,
    Alert,
    AlertIcon,
    Tr,
} from '@chakra-ui/react';
import React, { useState, useMemo, useRef } from 'react';
import { TbSearch, TbTrash } from 'react-icons/tb';
import { useSettingsDraft } from '../../pages/settings/SettingsDraftContext';
import { RekeySettings, PartType } from '../../hooks/useGetSettings';
import { getPartTypeLabel } from '../../utils/chartUtils';
import PartBayButton from '../PartBayButton';
import ApuOverrideModal from './ApuOverrideModal';
import type { ApuCustomEntry } from './apuOverrideDraft';
import {
    SettingsCard,
    SettingsRow,
    SettingsSubTable,
    SettingsTd,
    SettingsTh,
    useSubTablePagination,
} from './primitives';

const APU_PART_TYPE_OPTIONS: PartType[] = ['oem', 'oe-surplus', 'a/m'];

// const DEFAULT_PRICE_MATCH_NOTE: NonNullable<RekeySettings['modifyPriceMatchedDescriptions']> = {
//     enabled: false,
//     text: '(PRICE MATCHED)',
// };

const DEFAULT_APU_MAXIMIZATION: RekeySettings['apuMaximization'] = {
    default: 'oem',
    custom: [],
};

interface OemOption {
    _id: string;
    name: string;
}

interface RekeyProps {
    data: RekeySettings;
    managementSystem: 'ccc' | 'nex';
    oemOptions: Array<OemOption>;
}

const Rekey = ({ data, managementSystem, oemOptions }: RekeyProps) => {
    // Edits are buffered on the shared settings draft, so the page's save bar
    // writes every change on this screen in one request.
    const { getDomain, setDomain } = useSettingsDraft();
    const localData = getDomain('rekeySettings', data) ?? data;

    // Modal state
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isManualPOAlertOpen, setIsManualPOAlertOpen] = useState(false);
    const [modalSearchTerm, setModalSearchTerm] = useState('');
    const [pendingMakeIds, setPendingMakeIds] = useState<Set<string>>(new Set());
    const cancelManualPORef = useRef<HTMLButtonElement>(null);

    // APU Maximization state
    const [isApuModalOpen, setIsApuModalOpen] = useState(false);

    const handleToggle = (key: keyof RekeySettings, value: boolean) => {
        setDomain('rekeySettings', { ...localData, [key]: value });
    };

    // Turning the A/M cert flag off also hides and clears the CAPA
    // description toggle (ENG-1800 / ENG-1816).
    const handleFlagAftermarketCertificationsToggle = (isChecked: boolean) => {
        setDomain('rekeySettings', {
            ...localData,
            flagAftermarketCertifications: isChecked,
            ...(!isChecked ? { addPartCertification: false } : {}),
        });
    };

    // const priceMatchNote = localData.modifyPriceMatchedDescriptions ?? DEFAULT_PRICE_MATCH_NOTE;

    // const handlePriceMatchNoteToggle = (enabled: boolean) => {
    //     setDomain('rekeySettings', {
    //         ...localData,
    //         modifyPriceMatchedDescriptions: { ...priceMatchNote, enabled },
    //     });
    // };

    // const handlePriceMatchNoteTextChange = (text: string) => {
    //     setDomain('rekeySettings', {
    //         ...localData,
    //         modifyPriceMatchedDescriptions: { ...priceMatchNote, text },
    //     });
    // };

    // const handlePriceMatchNoteTextBlur = () => {
    //     const text = priceMatchNote.text.trim() || DEFAULT_PRICE_MATCH_NOTE.text;
    //     if (text === priceMatchNote.text) return;
    //     setDomain('rekeySettings', {
    //         ...localData,
    //         modifyPriceMatchedDescriptions: { ...priceMatchNote, text },
    //     });
    // };

    // const handleManualPOToggle = (isChecked: boolean) => {
    //     if (isChecked && !localData.createManualPO) {
    //         setIsManualPOAlertOpen(true);
    //         return;
    //     }

    //     handleToggle('createManualPO', isChecked);
    // };

    const handleConfirmManualPO = () => {
        setIsManualPOAlertOpen(false);
        handleToggle('createManualPO', true);
    };

    // const handleCutoffChange = (field: 'hour' | 'minute', value: number) => {
    //     setDomain('rekeySettings', { ...localData, cutoff: { ...localData.cutoff, [field]: value } });
    // };

    const availableMakes = useMemo(() => {
        const excludedIds = new Set(localData.excludedMakes.map(m => m.makeId));
        return oemOptions
            .filter(o => o.name && o.name.trim() !== '')
            .filter(o => !excludedIds.has(o._id))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [oemOptions, localData.excludedMakes]);

    const filteredModalMakes = useMemo(() => {
        const search = modalSearchTerm.toLowerCase();
        return availableMakes.filter(m => m.name.toLowerCase().includes(search));
    }, [availableMakes, modalSearchTerm]);

    const excludedMakeRows = useMemo(
        () => [...localData.excludedMakes].sort((a, b) => a.name.localeCompare(b.name)),
        [localData.excludedMakes],
    );
    const excludedMakePaging = useSubTablePagination(excludedMakeRows);

    const handleRemoveExcludedMake = (makeId: string) => {
        const newExcluded = localData.excludedMakes.filter(m => m.makeId !== makeId);
        setDomain('rekeySettings', { ...localData, excludedMakes: newExcluded });
    };

    const openModal = () => {
        setModalSearchTerm('');
        setPendingMakeIds(new Set());
        setIsModalOpen(true);
    };

    const togglePendingMake = (makeId: string) => {
        setPendingMakeIds(prev => {
            const next = new Set(prev);
            if (next.has(makeId)) {
                next.delete(makeId);
            } else {
                next.add(makeId);
            }
            return next;
        });
    };

    const handleSaveExclusions = () => {
        if (pendingMakeIds.size === 0) return;
        const newMakes = Array.from(pendingMakeIds)
            .map(id => {
                const make = oemOptions.find(o => o._id === id);
                return make ? { makeId: make._id, name: make.name } : null;
            })
            .filter(Boolean) as Array<{ makeId: string; name: string; }>;

        const newExcluded = [...localData.excludedMakes, ...newMakes];
        setDomain('rekeySettings', { ...localData, excludedMakes: newExcluded });
        setIsModalOpen(false);
    };

    // const formattedCutoff = useMemo(() => {
    //     const { hour, minute } = localData.cutoff;
    //     return `${hour}:${minute.toString().padStart(2, '0')} PM`;
    // }, [localData.cutoff]);

    // ---------- APU Maximization helpers ----------
    const apuMaximization = useMemo(
        () => localData.apuMaximization ?? DEFAULT_APU_MAXIMIZATION,
        [localData.apuMaximization],
    );

    // Rows keep their index in the stored array: paging slices the list, but
    // the part type and remove handlers still address the original entry.
    const apuCustomRows = useMemo(
        () => apuMaximization.custom.map((entry, index) => ({ entry, index })),
        [apuMaximization.custom],
    );
    const apuCustomPaging = useSubTablePagination(apuCustomRows);

    const persistApuMaximization = (next: RekeySettings['apuMaximization']) => {
        setDomain('rekeySettings', { ...localData, apuMaximization: next });
    };

    const handleApuDefaultChange = (partType: PartType) => {
        persistApuMaximization({ ...apuMaximization, default: partType });
    };

    const handleApuCustomPartTypeChange = (index: number, partType: PartType) => {
        const nextCustom = apuMaximization.custom.map((entry, i) =>
            i === index ? { ...entry, partType } : entry,
        );
        persistApuMaximization({ ...apuMaximization, custom: nextCustom });
    };

    const handleApuRemoveCustom = (index: number) => {
        const nextCustom = apuMaximization.custom.filter((_, i) => i !== index);
        persistApuMaximization({ ...apuMaximization, custom: nextCustom });
    };

    const handleAddApuOverrides = (nextCustom: Array<ApuCustomEntry>) => {
        persistApuMaximization({ ...apuMaximization, custom: nextCustom });
    };

    const apuWillBeIgnored = managementSystem === 'ccc' && localData.createManualPO;

    return (
        <VStack spacing={6} align="stretch" pb={16}>
            <SettingsCard
                title="Re-keying"
                description="Configure re-keying behavior for your orders."
            >
                <SettingsRow
                    label="Enable re-keying"
                    description="When enabled, orders will be automatically re-keyed into the estimating system."
                    control={
                        <Switch
                            size="lg"
                            isChecked={localData.shouldRekey}
                            onChange={(e) => handleToggle('shouldRekey', e.target.checked)}
                            colorScheme="partbay-blue"
                        />
                    }
                />
                {localData.shouldRekey && (
                    <SettingsRow
                        label="Re-key non-DRP jobs"
                        description="When enabled, non-DRP jobs will also be automatically re-keyed into the estimating system."
                        control={
                            <Switch
                                size="lg"
                                isChecked={localData.shouldRekeyNonDrp}
                                onChange={(e) => handleToggle('shouldRekeyNonDrp', e.target.checked)}
                                colorScheme="partbay-blue"
                            />
                        }
                    />
                )}
                <Box opacity={localData.shouldRekey ? 1 : 0.5}>
                    <SettingsRow
                        label="Re-key quotes only"
                        description="When enabled, potential totals and quotes only will be automatically re-keyed with the automatic selections when the quoting period is finished."
                        control={
                            <Switch
                                size="lg"
                                isChecked={localData.autoRekeyQuoteOnly}
                                onChange={(e) => handleToggle('autoRekeyQuoteOnly', e.target.checked)}
                                colorScheme="partbay-blue"
                                isDisabled={!localData.shouldRekey}
                            />
                        }
                    />
                </Box>
                {/* {managementSystem === 'ccc' && (
                    <Box opacity={localData.shouldRekey ? 1 : 0.5}>
                        <SettingsRow
                            label="Create manual POs"
                            description="When enabled, Yaxle will create manual POs in CCC after re-keying."
                            control={
                                <Switch
                                    size="lg"
                                    isChecked={localData.createManualPO}
                                    onChange={(e) => handleManualPOToggle(e.target.checked)}
                                    colorScheme="partbay-blue"
                                    isDisabled={!localData.shouldRekey}
                                />
                            }
                        />
                    </Box>
                )} */}
                <Box opacity={localData.shouldRekey ? 1 : 0.5}>
                    <SettingsRow
                        label="Flag creation for re-keying A/M certifications"
                        description="When enabled, re-keying a part to A/M requires the certification to also carry over (e.g., CAPA/KeysIQ) to be considered successful, otherwise it's flagged for review."
                        control={
                            <Switch
                                size="lg"
                                isChecked={localData.flagAftermarketCertifications !== false}
                                onChange={(e) => handleFlagAftermarketCertificationsToggle(e.target.checked)}
                                colorScheme="partbay-blue"
                                isDisabled={!localData.shouldRekey}
                            />
                        }
                    />
                </Box>
                {/* <Box opacity={localData.shouldRekey ? 1 : 0.5}>
                    <SettingsRow
                        label="Modify price matched part descriptions"
                        description='When a part is bought as OEM but it was price matched to a non-OEM part, this will add the text of your choice to the beginning of the part description. Skipped if the description already says "price matched".'
                        control={
                            <Switch
                                size="lg"
                                isChecked={priceMatchNote.enabled}
                                onChange={(e) => handlePriceMatchNoteToggle(e.target.checked)}
                                colorScheme="partbay-blue"
                                isDisabled={!localData.shouldRekey}
                            />
                        }
                    >
                        {priceMatchNote.enabled && (
                            <HStack justify="space-between" align="center">
                                <Text fontSize="sm" color="gray.600">
                                    Text added to the beginning of the part description
                                </Text>
                                <Input
                                    size="sm"
                                    w="240px"
                                    value={priceMatchNote.text}
                                    onChange={(e) => handlePriceMatchNoteTextChange(e.target.value)}
                                    onBlur={handlePriceMatchNoteTextBlur}
                                    placeholder={DEFAULT_PRICE_MATCH_NOTE.text}
                                    bg="white"
                                    borderColor="gray.300"
                                    isDisabled={!localData.shouldRekey}
                                />
                            </HStack>
                        )}
                    </SettingsRow>
                </Box> */}
                {/* {localData.flagAftermarketCertifications !== false && (
                    <Box opacity={localData.shouldRekey ? 1 : 0.5}>
                        <SettingsRow
                            label="Add part certification"
                            description="Requires Flag creation for re-keying A/M certifications. This will add CAPA to the beginning of the part description in the estimate for CAPA-certified A/M purchased parts. Skipped if the description already contains CAPA."
                            control={
                                <Switch
                                    size="lg"
                                    isChecked={localData.addPartCertification === true}
                                    onChange={(e) => handleToggle('addPartCertification', e.target.checked)}
                                    colorScheme="partbay-blue"
                                    isDisabled={!localData.shouldRekey}
                                />
                            }
                        />
                    </Box>
                )} */}
                {/* <Box opacity={localData.shouldRekey ? 1 : 0.5}>
                    <SettingsRow
                        label="Re-key cutoff time"
                        description={`Estimates will not be re-keyed after ${formattedCutoff} local time to avoid leaving estimates unlocked overnight. Estimates ready to re-key after the cutoff will be re-keyed the following morning.`}
                        control={
                            <HStack spacing={2}>
                                <Select
                                    size="sm"
                                    w="80px"
                                    value={localData.cutoff.hour}
                                    onChange={(e) => handleCutoffChange('hour', parseInt(e.target.value))}
                                    isDisabled={!localData.shouldRekey}
                                >
                                    {Array.from({ length: 11 }, (_, i) => i + 1).map((h) => (
                                        <option key={h} value={h}>{h}</option>
                                    ))}
                                </Select>
                                <Text fontWeight="medium">:</Text>
                                <Select
                                    size="sm"
                                    w="80px"
                                    value={localData.cutoff.minute}
                                    onChange={(e) => handleCutoffChange('minute', parseInt(e.target.value))}
                                    isDisabled={!localData.shouldRekey}
                                >
                                    {Array.from({ length: 12 }, (_, i) => i * 5).map((m) => (
                                        <option key={m} value={m}>
                                            {m.toString().padStart(2, '0')}
                                        </option>
                                    ))}
                                </Select>
                                <Text fontSize="sm" fontWeight="medium" color="gray.600">PM</Text>
                            </HStack>
                        }
                    />
                </Box> */}
            </SettingsCard>

            <Box opacity={localData.shouldRekey ? 1 : 0.5}>
                <SettingsCard
                    title="Excluded makes"
                    description="Estimates for these vehicle makes will not be automatically re-keyed."
                    headerAction={
                        <PartBayButton
                            variant="solid"
                            colorScheme="partbay-blue"
                            onClick={openModal}
                            text="Exclude Make"
                            size="sm"
                            isDisabled={!localData.shouldRekey || availableMakes.length === 0}
                        />
                    }
                >
                    {/* py matches SettingsRow so the table lines up with the
                        rows of the cards above it. */}
                    <Box py={4}>
                        <SettingsSubTable
                            isInset
                            currentPage={excludedMakePaging.currentPage}
                            totalPages={excludedMakePaging.totalPages}
                            onPageChange={excludedMakePaging.setCurrentPage}
                            head={
                                <>
                                    <SettingsTh>Make</SettingsTh>
                                    <SettingsTh textAlign="center" w="100px">
                                        Actions
                                    </SettingsTh>
                                </>
                            }
                        >
                            {excludedMakeRows.length === 0 ? (
                                <Tr>
                                    <SettingsTd colSpan={2} py={6} color="gray.500">
                                        No makes are excluded from re-keying.
                                    </SettingsTd>
                                </Tr>
                            ) : (
                                excludedMakePaging.pageItems.map((make) => (
                                    <Tr key={make.makeId}>
                                        <SettingsTd fontWeight="medium" color="gray.800">
                                            {make.name}
                                        </SettingsTd>
                                        <SettingsTd textAlign="center">
                                            <IconButton
                                                aria-label={`Remove ${make.name}`}
                                                icon={
                                                    <Icon
                                                        as={TbTrash as React.ElementType}
                                                        boxSize={4}
                                                    />
                                                }
                                                size="sm"
                                                variant="ghost"
                                                colorScheme="red"
                                                isDisabled={!localData.shouldRekey}
                                                onClick={() => handleRemoveExcludedMake(make.makeId)}
                                            />
                                        </SettingsTd>
                                    </Tr>
                                ))
                            )}
                        </SettingsSubTable>
                    </Box>
                </SettingsCard>
            </Box>

            {/* ----- APU Maximization ----- */}
            <Box opacity={localData.shouldRekey ? 1 : 0.5}>
                <SettingsCard
                    title="APU maximization"
                    description="Defines what part type we re-key price-matched OEM parts as. The default applies to all insurance carriers; custom rules below override the default for the listed carriers."
                >
                    <Box py={4}>
                        <Alert
                            status="warning"
                            borderRadius="md"
                            bg="orange.50"
                            border="1px solid"
                            borderColor="orange.200"
                            py={2}
                            alignItems="flex-start"
                        >
                            <AlertIcon color="orange.500" boxSize={4} mt="2px" />
                            <Text fontSize="xs" color="orange.800">
                                {apuWillBeIgnored ? (
                                    <>
                                        <strong>Heads up — currently inactive.</strong>{' '}
                                        Because <strong>Create Manual POs</strong> is enabled, all OEM parts will be re-keyed as OEM and these settings will be ignored.
                                        {' '}It is also ignored when the estimate&apos;s repair facility is located in California.
                                    </>
                                ) : (
                                    <>
                                        If the estimate&apos;s repair facility is located in <strong>California</strong>, this setting is ignored and all OEM parts will be re-keyed as OEM.
                                    </>
                                )}
                            </Text>
                        </Alert>
                    </Box>

                    <SettingsRow
                        label="Default"
                        description="Applies to all insurance carriers unless overridden below."
                        control={
                            <Select
                                size="sm"
                                w="160px"
                                value={apuMaximization.default}
                                onChange={(e) => handleApuDefaultChange(e.target.value as PartType)}
                                isDisabled={!localData.shouldRekey}
                                bg="white"
                            >
                                {APU_PART_TYPE_OPTIONS.map((pt) => (
                                    <option key={pt} value={pt}>{getPartTypeLabel(pt)}</option>
                                ))}
                            </Select>
                        }
                    />

                    <SettingsRow
                        label="Custom overrides"
                        control={
                            <PartBayButton
                                variant="solid"
                                colorScheme="partbay-blue"
                                onClick={() => setIsApuModalOpen(true)}
                                text="Add Override"
                                size="sm"
                                isDisabled={!localData.shouldRekey}
                            />
                        }
                    >
                        <SettingsSubTable
                            isInset
                            currentPage={apuCustomPaging.currentPage}
                            totalPages={apuCustomPaging.totalPages}
                            onPageChange={apuCustomPaging.setCurrentPage}
                            head={
                                <>
                                    <SettingsTh>Carrier</SettingsTh>
                                    <SettingsTh w="180px">Part Type</SettingsTh>
                                    <SettingsTh textAlign="center" w="100px">
                                        Actions
                                    </SettingsTh>
                                </>
                            }
                        >
                            {apuCustomRows.length === 0 ? (
                                <Tr>
                                    <SettingsTd colSpan={3} py={6} color="gray.500">
                                        No custom overrides — every carrier uses the default.
                                    </SettingsTd>
                                </Tr>
                            ) : (
                                apuCustomPaging.pageItems.map(({ entry, index }) => (
                                    <Tr key={`apu-custom-${index}`}>
                                        {/* Plain text, not chips: a carrier name
                                            is the row's subject, not a badge. */}
                                        <SettingsTd fontWeight="medium" color="gray.800">
                                            {entry.insuranceCompanies.map((co) => co.name).join(', ')}
                                        </SettingsTd>
                                        <SettingsTd>
                                            <Select
                                                size="sm"
                                                w="150px"
                                                value={entry.partType}
                                                onChange={(e) => handleApuCustomPartTypeChange(index, e.target.value as PartType)}
                                                isDisabled={!localData.shouldRekey}
                                                bg="white"
                                            >
                                                {APU_PART_TYPE_OPTIONS.map((pt) => (
                                                    <option key={pt} value={pt}>{getPartTypeLabel(pt)}</option>
                                                ))}
                                            </Select>
                                        </SettingsTd>
                                        <SettingsTd textAlign="center">
                                            <IconButton
                                                aria-label={`Remove ${entry.insuranceCompanies.map((co) => co.name).join(', ')} override`}
                                                icon={
                                                    <Icon
                                                        as={TbTrash as React.ElementType}
                                                        boxSize={4}
                                                    />
                                                }
                                                size="sm"
                                                variant="ghost"
                                                colorScheme="red"
                                                isDisabled={!localData.shouldRekey}
                                                onClick={() => handleApuRemoveCustom(index)}
                                            />
                                        </SettingsTd>
                                    </Tr>
                                ))
                            )}
                        </SettingsSubTable>
                    </SettingsRow>
                </SettingsCard>
            </Box>

            {/* Exclude Make Modal */}
            <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} isCentered size="md">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader fontSize="md" fontWeight="semibold" color="gray.800" py={4}>
                        Exclude Makes from Re-Keying
                    </ModalHeader>
                    <ModalCloseButton top={3} />
                    <ModalBody py={4}>
                        <VStack spacing={3} align="stretch">
                            <InputGroup size="sm">
                                <InputLeftElement pointerEvents="none">
                                    <Icon as={TbSearch as React.ElementType} color="gray.400" />
                                </InputLeftElement>
                                <Input
                                    placeholder="Search makes..."
                                    value={modalSearchTerm}
                                    onChange={(e) => setModalSearchTerm(e.target.value)}
                                    bg="white"
                                    borderColor="gray.300"
                                />
                            </InputGroup>
                            <Box
                                border="1px solid"
                                borderColor="gray.200"
                                borderRadius="md"
                                maxH="340px"
                                overflowY="auto"
                                p={2}
                            >
                                {filteredModalMakes.length === 0 ? (
                                    <Text fontSize="sm" color="gray.500" py={4} textAlign="center">
                                        {modalSearchTerm ? 'No makes found' : 'All makes already excluded'}
                                    </Text>
                                ) : (
                                    <VStack align="stretch" spacing={2}>
                                        {filteredModalMakes.map(make => (
                                            <Checkbox
                                                key={make._id}
                                                isChecked={pendingMakeIds.has(make._id)}
                                                onChange={() => togglePendingMake(make._id)}
                                                size="sm"
                                                colorScheme="partbay-blue"
                                            >
                                                <Text fontSize="sm">{make.name}</Text>
                                            </Checkbox>
                                        ))}
                                    </VStack>
                                )}
                            </Box>
                            {pendingMakeIds.size > 0 && (
                                <Text fontSize="xs" color="gray.500">
                                    {pendingMakeIds.size} make{pendingMakeIds.size > 1 ? 's' : ''} selected
                                </Text>
                            )}
                        </VStack>
                    </ModalBody>
                    <ModalFooter>
                        <HStack spacing={2}>
                            <Button
                                size="action"
                                variant="outlineGray"
                                onClick={() => setIsModalOpen(false)}
                            >
                                Cancel
                            </Button>
                            <PartBayButton
                                size="action"
                                variant="solid"
                                colorScheme="partbay-blue"
                                onClick={handleSaveExclusions}
                                isDisabled={pendingMakeIds.size === 0}
                                text="Exclude Selected"
                            />
                        </HStack>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            <ApuOverrideModal
                isOpen={isApuModalOpen}
                onClose={() => setIsApuModalOpen(false)}
                custom={apuMaximization.custom}
                partTypeOptions={APU_PART_TYPE_OPTIONS}
                defaultPartType={apuMaximization.default}
                onSubmit={handleAddApuOverrides}
            />

            <AlertDialog
                isOpen={isManualPOAlertOpen}
                leastDestructiveRef={cancelManualPORef}
                onClose={() => setIsManualPOAlertOpen(false)}
                isCentered
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="semibold">
                            Enable Manual PO Creation?
                        </AlertDialogHeader>
                        <AlertDialogCloseButton />
                        <AlertDialogBody>
                            The expected delivery data on the created POs may be innacurate. If the initial PO delivery dates are important for your organization, we recommend keeping this turned off
                        </AlertDialogBody>
                        <AlertDialogFooter>
                            <HStack spacing={3}>
                                <Button size="action" variant="outlineGray" ref={cancelManualPORef} onClick={() => setIsManualPOAlertOpen(false)}>
                                    Cancel
                                </Button>
                                <PartBayButton
                                    size="action"
                                    variant="solid"
                                    colorScheme="partbay-blue"
                                    onClick={handleConfirmManualPO}
                                    text="Confirm"
                                />
                            </HStack>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </VStack>
    );
};

export default Rekey;
